import * as React from "react";
import { Pencil, Trash2, DollarSign } from "lucide-react";

import { cn } from "../../lib/utils";
import { Card, CardContent } from "./card";
import { Badge, DotBadge } from "./badge";
import { Button } from "./button";
import { CurrencyValue, PercentValue } from "./value-display";
import { Text, MonoText } from "./typography";
import { StalenessIndicator } from "./StalenessIndicator";

/**
 * HoldingCard - Mobile-friendly card view for holdings
 *
 * Displays holding information in a compact card format optimized for mobile.
 * Touch-friendly with minimum 44x44px touch targets.
 *
 * @param {Object} props
 * @param {Object} props.holding - Normalized holding data
 * @param {boolean} props.isLiability - Whether this is a liability
 * @param {Function} props.onEdit - Edit callback
 * @param {Function} props.onDelete - Delete callback
 * @param {Function} props.onSell - Sell callback (optional)
 * @param {Function} props.onClick - Card click callback (optional)
 */

// Map asset types to badge variants
const getAssetTypeBadgeVariant = (assetType) => {
  const variants = {
    'Stock': 'info',
    'ETF': 'info',
    'Cryptocurrency': 'warning',
    'Precious Metal': 'success',
    'Cash/Bank Account': 'secondary',
    'Real Estate': 'success',
    'Real Estate & Property': 'success',
    'Vehicle': 'secondary',
    'Equipment': 'secondary',
    'Other High-Value Asset': 'secondary',
    'Mortgage': 'destructive',
    'Loan': 'destructive',
    'Credit Card Debt': 'destructive',
    'Other Liability': 'destructive',
  };
  return variants[assetType] || 'secondary';
};

export function HoldingCard({
  holding,
  isLiability = false,
  onEdit,
  onDelete,
  onSell,
  onClick,
  className,
}) {
  const showSellButton = !isLiability && !holding._raw?.useManualValue && onSell;

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit?.(holding._raw);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(holding._raw);
  };

  const handleSell = (e) => {
    e.stopPropagation();
    onSell?.(holding._raw);
  };

  return (
    <Card
      className={cn(
        "transition-all duration-200 active:scale-[0.98]",
        onClick && "cursor-pointer hover:border-primary/30",
        className
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        {/* Header Row: Name + Value */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Text className="font-semibold truncate text-base" title={holding.name}>
                {holding.name}
              </Text>
              <StalenessIndicator holding={holding._raw} size="sm" />
              {holding.ticker && holding.ticker !== '-' && (
                <Badge variant="outline" size="sm" className="shrink-0">
                  {holding.ticker}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Badge
                variant={getAssetTypeBadgeVariant(holding.assetType)}
                size="sm"
              >
                {holding.assetType}
              </Badge>
              {holding.platform && holding.platform !== '-' && (
                <DotBadge
                  variant={
                    isLiability
                      ? 'destructive'
                      : holding.gain >= 0
                      ? 'success'
                      : 'destructive'
                  }
                  size="sm"
                >
                  {holding.platform}
                </DotBadge>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <CurrencyValue
              value={Math.abs(holding.totalValue)}
              currency="EUR"
              size="lg"
              className="font-bold"
              colorize={isLiability}
            />
          </div>
        </div>

        {/* Details Row */}
        <div className="mt-3 pt-3 border-t border-border/50">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {/* Units */}
            <div>
              <Text variant="secondary" size="xs" className="uppercase tracking-wider">
                {isLiability ? 'Amount' : 'Units'}
              </Text>
              <MonoText className="font-medium">
                {isLiability ? (
                  <CurrencyValue
                    value={Math.abs(holding.totalValue || 0)}
                    currency="EUR"
                    size="sm"
                  />
                ) : (
                  holding.units?.toLocaleString(undefined, { maximumFractionDigits: 4 })
                )}
              </MonoText>
            </div>

            {/* Average Cost / Original Amount */}
            {!isLiability && (
              <div>
                <Text variant="secondary" size="xs" className="uppercase tracking-wider">
                  Avg. Cost
                </Text>
                <MonoText className="font-medium">
                  <CurrencyValue
                    value={holding.purchasePrice}
                    currency={holding.currency}
                    size="sm"
                  />
                </MonoText>
              </div>
            )}

            {/* Current Price */}
            {!isLiability && (
              <div>
                <Text variant="secondary" size="xs" className="uppercase tracking-wider">
                  Current Price
                </Text>
                <MonoText className="font-medium">
                  <CurrencyValue
                    value={holding.currentPrice}
                    currency={holding.tradingCurrency}
                    size="sm"
                  />
                </MonoText>
              </div>
            )}

            {/* Total Gain */}
            {!isLiability && (
              <div>
                <Text variant="secondary" size="xs" className="uppercase tracking-wider">
                  Gain/Loss
                </Text>
                <div className="flex items-center gap-1">
                  <CurrencyValue
                    value={holding.gain}
                    currency="EUR"
                    size="sm"
                    colorize
                    className="font-medium"
                  />
                  <PercentValue
                    value={holding.gainPercent}
                    size="xs"
                    showTrend
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions Row */}
        <div className="mt-3 pt-3 border-t border-border/50 flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-11 px-3 min-w-[44px]"
            onClick={handleEdit}
            aria-label={`Edit ${holding.name}`}
          >
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>

          {showSellButton && (
            <Button
              variant="ghost"
              size="sm"
              className="h-11 px-3 min-w-[44px] text-green-600 hover:text-green-700 hover:bg-green-50"
              onClick={handleSell}
              aria-label={`Sell ${holding.name}`}
            >
              <DollarSign className="h-4 w-4 mr-2" />
              Sell
            </Button>
          )}

          <Button
            variant="ghost"
            size="sm"
            className="h-11 px-3 min-w-[44px] text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleDelete}
            aria-label={`Delete ${holding.name}`}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default HoldingCard;
